/**
 * LOGIN PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - True Liquid Glass material with lensing and refraction
 * - OTP verification flow with animated inputs
 * - Fluid spring animations throughout
 * - Morphing tab switcher with pill indicator
 * - Specular highlights and ambient glow
 * - Responsive layout with dark/light mode
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Lock, ArrowRight, AlertCircle, ArrowLeft, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Toast } from '../components/ui/Toast'
import { cn } from '../lib/utils'

// Spring config for animations
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

export function LoginPage() {
  const { login, verifyOTP, error } = useAuth()

  // Form state - restore from sessionStorage if available
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login')
  const [email, setEmail] = useState(() => sessionStorage.getItem('loginEmail') || '')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')

  // OTP state - restore from sessionStorage if available
  const [showOTP, setShowOTP] = useState(() => sessionStorage.getItem('showOTP') === 'true')
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', ''])
  const [otpError, setOtpError] = useState('')

  // Local loading states
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false)

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  console.log('LoginPage render - showOTP:', showOTP, 'email:', email)

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('handleSubmit called with email:', email)

    setIsLoggingIn(true)
    try {
      console.log('Calling login...')
      const result = await login(email, password)
      console.log('Login result:', result)

      if (result.requiresOTP) {
        console.log('OTP required, showing OTP screen')
        // Save to sessionStorage BEFORE setting state
        sessionStorage.setItem('showOTP', 'true')
        sessionStorage.setItem('loginEmail', email)
        console.log('Saved to sessionStorage - showOTP: true, email:', email)
        setShowOTP(true)
        console.log('setShowOTP(true) called, showOTP should now be true')
      } else {
        console.log('No OTP required - unexpected!')
      }
    } catch (err) {
      console.error('Login error:', err)
      setToast({
        message: error || 'Login failed. Please try again.',
        type: 'error'
      })
    } finally {
      setIsLoggingIn(false)
    }
  }

  /**
   * Handle OTP input
   */
  const handleOTPChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newOtpDigits = [...otpDigits]
    newOtpDigits[index] = value

    setOtpDigits(newOtpDigits)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }

    // Auto-submit if all digits filled
    if (index === 5 && value && newOtpDigits.every(d => d)) {
      handleOTPSubmit(newOtpDigits.join(''))
    }
  }

  /**
   * Handle OTP backspace
   */
  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      prevInput?.focus()
    }
  }

  /**
   * Handle OTP paste
   */
  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').trim()

    // Only allow 6-digit numbers
    if (!/^\d{6}$/.test(pastedData)) return

    const newOtpDigits = pastedData.split('')
    setOtpDigits(newOtpDigits)

    // Focus last input and auto-submit
    const lastInput = document.getElementById('otp-5')
    lastInput?.focus()
    handleOTPSubmit(pastedData)
  }

  /**
   * Submit OTP
   */
  const handleOTPSubmit = async (otp: string) => {
    console.log('handleOTPSubmit called with OTP:', otp)
    setIsVerifyingOTP(true)
    try {
      setOtpError('')
      console.log('Calling verifyOTP...')
      const result = await verifyOTP(email, otp)
      console.log('verifyOTP returned:', result)
      // Clear session storage on successful OTP verification
      sessionStorage.removeItem('loginEmail')
      sessionStorage.removeItem('showOTP')
      // Don't show toast - let App component handle redirect to dashboard
    } catch (err) {
      console.error('handleOTPSubmit error:', err)
      setOtpError('Invalid verification code. Please try again.')
    } finally {
      setIsVerifyingOTP(false)
    }
  }

  /**
   * Back to login form
   */
  const handleBackToLogin = () => {
    setShowOTP(false)
    setOtpDigits(['', '', '', '', '', ''])
    setOtpError('')
    // Clear session storage when going back
    sessionStorage.removeItem('showOTP')
  }

  return (
    <div className="min-h-screen bg-light-bg-secondary dark:bg-dark-bg-primary flex items-center justify-center p-4 relative overflow-hidden">

      {/* Animated background with ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large ambient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue/20 dark:bg-blue-dark/10 rounded-full blur-[100px]"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple/20 dark:bg-purple-dark/10 rounded-full blur-[100px]"
          animate={{
            x: [0, -30, 0],
            y: [0, 20, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        {/* Additional subtle accents */}
        <motion.div
          className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-green/10 dark:bg-green-dark/5 rounded-full blur-[80px]"
          animate={{
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Login Container */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={springConfig}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo with Liquid Glass effect */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ ...springConfig, delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="relative w-20 h-20 rounded-[22px] overflow-hidden shadow-liquid-lg dark:shadow-dark-liquid-lg group">
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue via-blue-dark to-indigo" />
            {/* Specular highlight */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent" />
            {/* Icon */}
            <div className="relative w-full h-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            {/* Animated shimmer */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            />
          </div>
        </motion.div>

        {/* Title with stagger animation */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springConfig, delay: 0.15 }}
        >
          <h1 className="text-largeTitle font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight mb-2">
            Partners Portal
          </h1>
          <p className="text-body text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
            Secure access to GHL Financial premium accounts
          </p>
        </motion.div>

        {/* Tab Switcher with animated pill indicator */}
        <AnimatePresence>
          {!showOTP && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={springConfig}
              className="flex liquid-material-regular rounded-2xl p-1.5 mb-6 relative"
            >
              {/* Animated pill background */}
              <motion.div
                className="absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-gradient-to-b from-blue to-blue-dark rounded-xl shadow-glass-sm"
                animate={{ x: activeTab === 'login' ? 0 : 'calc(100% + 6px)' }}
                transition={springConfig}
              />

              <button
                onClick={() => setActiveTab('login')}
                className={cn(
                  "flex-1 py-2.5 px-4 rounded-xl text-subheadline font-semibold tracking-tight transition-colors duration-fast relative z-10",
                  activeTab === 'login'
                    ? 'text-white'
                    : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary'
                )}
              >
                Sign In
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={cn(
                  "flex-1 py-2.5 px-4 rounded-xl text-subheadline font-semibold tracking-tight transition-colors duration-fast relative z-10",
                  activeTab === 'signup'
                    ? 'text-white'
                    : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary'
                )}
              >
                Sign Up
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Forms Container - Liquid Glass card */}
        <motion.div
          className="liquid-material-thick liquid-lens rounded-3xl p-8 shadow-liquid-xl dark:shadow-dark-liquid-xl border border-white/10 dark:border-white/5 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springConfig, delay: 0.2 }}
        >
          {/* Specular highlight at top */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

          <AnimatePresence mode="wait">
            {/* Login/Signup Form */}
            {!showOTP ? (
              <motion.form
                key={activeTab}
                initial={{ opacity: 0, x: activeTab === 'login' ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: activeTab === 'login' ? 30 : -30 }}
                transition={springConfig}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Email */}
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  label="Email"
                  icon={<Mail className="w-5 h-5" />}
                  required
                />

                {/* Name (Signup only) */}
                <AnimatePresence>
                  {activeTab === 'signup' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={springConfig}
                    >
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your full name"
                        label="Full Name"
                        icon={<User className="w-5 h-5" />}
                        required
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Password */}
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  label="Password"
                  icon={<Lock className="w-5 h-5" />}
                  required
                />

                {/* Submit Button */}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    size="lg"
                    isLoading={isLoggingIn}
                    icon={<ArrowRight className="w-5 h-5" />}
                    iconPosition="right"
                  >
                    {activeTab === 'login' ? 'Sign In' : 'Create Account'}
                  </Button>
                </motion.div>
              </motion.form>
            ) : (
              /* OTP Verification Form */
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={springConfig}
                className="space-y-6"
              >
                {/* Back button */}
                <motion.button
                  onClick={handleBackToLogin}
                  disabled={isVerifyingOTP}
                  className="flex items-center gap-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
                  whileHover={{ x: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-subheadline font-medium">Back</span>
                </motion.button>

                {/* Header */}
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ ...springConfig, delay: 0.1 }}
                    className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-glass flex items-center justify-center"
                  >
                    <Mail className="w-8 h-8 text-green dark:text-green-dark" />
                  </motion.div>
                  <h2 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2 tracking-tight">
                    Check your email
                  </h2>
                  <p className="text-body text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
                    We've sent a 6-digit code to<br />
                    <strong className="text-light-text-primary dark:text-dark-text-primary">{email}</strong>
                  </p>
                </div>

                {/* OTP Inputs with enhanced styling */}
                <div className="flex gap-3 justify-center" onPaste={handleOTPPaste}>
                  {otpDigits.map((digit, index) => (
                    <motion.input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...springConfig, delay: 0.1 + index * 0.05 }}
                      className={cn(
                        "w-12 h-14 text-center text-title2 font-bold",
                        "liquid-material-regular rounded-xl",
                        "text-light-text-primary dark:text-dark-text-primary",
                        "border-2 border-transparent",
                        "focus:outline-none focus:border-blue/30 focus:shadow-glass-lg",
                        "focus:scale-110 transition-all duration-base",
                        "tracking-tight tabular-nums",
                        digit && "border-blue/20 shadow-glass-sm"
                      )}
                    />
                  ))}
                </div>

                {/* OTP Error */}
                <AnimatePresence>
                  {otpError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center justify-center gap-2 text-red dark:text-red-dark text-subheadline"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>{otpError}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Verify Button */}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Button
                    variant="primary"
                    fullWidth
                    size="lg"
                    onClick={() => handleOTPSubmit(otpDigits.join(''))}
                    isLoading={isVerifyingOTP}
                    disabled={otpDigits.some(d => !d)}
                  >
                    Verify Code
                  </Button>
                </motion.div>

                {/* Resend link */}
                <p className="text-center text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    className="text-blue dark:text-blue-dark font-medium hover:underline"
                  >
                    Resend
                  </button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <motion.p
          className="text-center mt-8 text-footnote text-light-text-tertiary dark:text-dark-text-tertiary tracking-tight"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          © {new Date().getFullYear()} GHL Financial. All rights reserved.
        </motion.p>
      </motion.div>

      {/* Toast Notifications */}
      <AnimatePresence>
        {toast && (
          <Toast
            id="login-toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
